import { Link } from 'react-router';
import { Button, SegmentGroup } from 'semantic-ui-react';
import { SuiTable, TitleSegment } from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';
import styled from '@emotion/styled';

import { Permission, LifeSkillsCertificationInformation } from 'api';
import { NextDateLabelIndicator, useExaminerId } from 'components';
import { useTableState } from 'hooks';
import { useLifeSkillsCertificationListQuery, useLifeSkillsRecertDatesQuery } from 'store';
import { TableState } from 'types';
import { formatDate } from 'utils';
import { COLUMNS } from './LifeSkillsCertificationList.columns';
import { getLifeSkillsLevelName } from 'const/life-skills-level/lifeSkillsLevelHelpers';

const EMPTY_LIST: LifeSkillsCertificationInformation[] = [];

const INITIAL_TABLE_STATE: TableState<LifeSkillsCertificationInformation, undefined> = {
  pageIndex: 0,
  pageSize: 50,
  sortBy: [],
  filters: undefined,
};

export const LifeSkillsCertificationList = () => {
  const examinerId = useExaminerId();
  const { permissions } = usePermissions();
  const { tableState, setTableState } = useTableState(INITIAL_TABLE_STATE);

  const canAdd = permissions.includes(Permission.UpdateCertificationHistory);

  const { data, isFetching } = useLifeSkillsCertificationListQuery({
    examinerId,
    page: tableState.pageIndex + 1,
    count: tableState.pageSize,
  });

  const { data: recertDates } = useLifeSkillsRecertDatesQuery({ examinerId });

  return (
    <SegmentGroup>
      <TitleSegment
        leftSection={
          recertDates && recertDates.length > 0 ? (
            <S.Wrapper>
              <strong>Next Life Skills Recertification:</strong>
              {recertDates.map((d) => (
                <NextDateLabelIndicator
                  key={getLifeSkillsLevelName(d.lifeSkillsLevel)}
                  label={getLifeSkillsLevelName(d.lifeSkillsLevel)}
                  value={formatDate(d.nextRecertificationDate)}
                />
              ))}
            </S.Wrapper>
          ) : (
            <>&nbsp;</>
          )
        }
        rightSection={
          canAdd ? <Button primary as={Link} to="add" icon="plus" content="Add new" /> : undefined
        }
      />
      <SuiTable
        columns={COLUMNS}
        data={data?.results ?? EMPTY_LIST}
        disableFilters
        disableSortBy
        onStateChange={setTableState}
        loading={isFetching}
        state={tableState}
        totalCount={data?.count}
        noResultsMsg="No certifications found"
        tableProps={{ 'data-testid': 'certification-list-table' }}
      />
    </SegmentGroup>
  );
};

const S = {
  Wrapper: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
};
